import { useEffect, useRef, useState } from "react";
import {
  AdVideoBodyLeftBottomItemWrapper,
  AdVideoBodyLeftBottomWrapper,
  AdVideoBodyLeftWrapper,
  AdVideoBodyRightListWrapper,
  AdVideoBodyRightTitle,
  AdVideoBodyRightWrapper,
  AdVideoBodyVideoTimeBox,
  AdVideoBodyVideoWrapper,
  AdVideoBodyWrapper,
  AdVideoHeaderLeftWrapper,
  AdVideoHeaderRightWrapper,
  AdVideoHeaderWrapper,
  AdVideoNoticeBox,
  AdVideoPrizeListItem,
  AdVideoPrizeListItemInfoBox,
  AdVideoWrapper,
} from "./style";
import { TournamentResponse } from "../../api/tournament";
import { TimerInfoState } from "../../pages/timer-controller";
import { MoneyInChip } from "../../pages/tournament/hook/useEditTournament";
import { convertToKoreanNumber, prize, prizeType } from "../../utils/timer";
import { pubState } from "../../recoil/pub.atom";
import { useRecoilValue } from "recoil";
import {
  AdVideoType,
  endSolutionAdVideoLog,
  getSolutionAdVideoList,
  SolutionAdVideoResponse,
  startSolutionAdVideoLog,
} from "../../api/solution-ad-video";

interface AdVideoProps {
  onClose: () => void;
  data: TournamentResponse | null;
  timeInfo: TimerInfoState;
  coverImage: string | null;
  stack: number;
  avgStack: number;
}

const AD_VIDEO_BASE_URL = "https://pub-a520873da0504254b3e2053ab7fbc536.r2.dev";

const AdVideo = ({
  onClose,
  data,
  timeInfo,
  coverImage,
  stack,
  avgStack,
}: AdVideoProps) => {
  const pub = useRecoilValue(pubState);
  const [videoList, setVideoList] = useState<SolutionAdVideoResponse[]>([]);
  const [prizeList, setPrizeList] = useState<MoneyInChip[]>([]);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [nextlevel, setNextLevel] = useState(0);
  const [nextlevelTime, setNextLevelTime] = useState("00:00");
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [nextSmallBlind, setNextSmallBlind] = useState(0);
  const [nextBigBlind, setNextBigBlind] = useState(0);
  const [nextAnte, setNextAnte] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [animClass, setAnimClass] = useState("timer-enter");
  const itemsPerPage = 10;
  const currentLogIdRef = useRef<number | null>(null);
  const isStartingLogRef = useRef(false);
  const isClosingRef = useRef(false);
  const currentFileNameRef = useRef<string | null>(null);

  const currentVideo = videoList[currentIndex];
  currentFileNameRef.current = currentVideo?.fileName ?? null;

  const getPlayerCount = () =>
    (data?.reBuyInUserCount ?? 0) +
    (data?.entryUserCount ?? 0) +
    (data?.adOnUserCount ?? 0);

  const getRemainingPlayerCount = () => data?.userCount ?? 0;

  const fetchVideoList = async () => {
    try {
      const list = await getSolutionAdVideoList(AdVideoType.PUB);
      setVideoList(list || []);
      setCurrentIndex(0);
    } catch (e) {
      console.error(e);
      setVideoList([]);
    }
  };

  const endCurrentLog = async () => {
    const logId = currentLogIdRef.current;
    if (!logId) return;

    currentLogIdRef.current = null;
    try {
      await endSolutionAdVideoLog(logId);
    } catch (e) {
      console.error(e);
    }
  };

  const startCurrentLog = async (fileName: string) => {
    if (!pub || !data?.id || isStartingLogRef.current || isClosingRef.current)
      return;
    if (currentLogIdRef.current) return;

    isStartingLogRef.current = true;
    try {
      const res = await startSolutionAdVideoLog({
        fileName,
        pubId: pub.id,
        pubName: pub.cafeName,
        tournamentId: data.id,
        playerCount: getRemainingPlayerCount(),
      });

      if (!res?.logId) return;

      if (isClosingRef.current || currentFileNameRef.current !== fileName) {
        await endSolutionAdVideoLog(res.logId).catch(console.error);
        return;
      }

      currentLogIdRef.current = res.logId;
    } catch (e) {
      console.error(e);
    } finally {
      isStartingLogRef.current = false;
    }
  };

  const handleClose = async () => {
    if (isClosingRef.current) return;
    isClosingRef.current = true;
    await endCurrentLog();
    onClose();
  };

  useEffect(() => {
    fetchVideoList();
  }, []);

  // 리스트가 변경되면 첫 페이지로 리셋
  useEffect(() => {
    setCurrentPage(0);
    setAnimClass("timer-enter");
  }, [data?.tournamentType, prizeList.length]);

  const getNextBlind = () => {
    if (!data) {
      return "";
    }

    if (!data.timerDto) {
      return `${`${convertToKoreanNumber(
        data.structure[1].smallBlind,
      )}/${convertToKoreanNumber(
        data.structure[1].bigBlind,
      )}(${convertToKoreanNumber(data.structure[1].ante)})`}`;
    }
    if (!timeInfo.currentLevel?.ord) {
      return "";
    }

    if (timeInfo.currentLevel.ord === data.structure.length) {
      return "THE END";
    }
    if (data.structure[timeInfo.currentLevel.ord].type === "BREAK") {
      return "BREAK";
    }

    setNextLevel(data.structure[timeInfo.currentLevel.ord].level ?? 0);
    setNextSmallBlind(
      data.structure[timeInfo.currentLevel.ord].smallBlind ?? 0,
    );
    setNextBigBlind(data.structure[timeInfo.currentLevel.ord].bigBlind ?? 0);
    setNextAnte(data.structure[timeInfo.currentLevel.ord].ante ?? 0);
  };

  useEffect(() => {
    getNextBlind();
  }, []);

  const updateIsSound = () => {
    const soundMap = localStorage.getItem("ad-sound");
    if (!soundMap) {
      localStorage.setItem("ad-sound", "true");
      setIsMuted(true);
      return;
    }

    const isMuted = soundMap === "true";
    setIsMuted(isMuted);
  };

  useEffect(() => {
    updateIsSound();

    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "soundMap") {
        updateIsSound();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    if (data) setPrizeList(data.moneyInChip);
  }, [data]);

  // 3초마다 페이지 전환 애니메이션
  useEffect(() => {
    if (prizeList.length <= itemsPerPage) {
      return;
    }

    const totalPages = Math.ceil(prizeList.length / itemsPerPage);
    const timeoutRef = { current: null as NodeJS.Timeout | null };

    const interval = setInterval(() => {
      setAnimClass("timer-exit");
      timeoutRef.current = setTimeout(() => {
        setCurrentPage((prev) => (prev + 1) % totalPages);
        setAnimClass("timer-enter");
      }, 300);
    }, 3000);

    return () => {
      clearInterval(interval);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [data?.tournamentType, prizeList.length]);

  useEffect(() => {
    if (!data || !timeInfo) return;

    if (timeInfo.currentLevel?.level !== null) {
      handleClose();
    }

    const nextOrd = timeInfo.currentLevel?.ord ?? 0;
    const structure = data.structure.find((item) => item.ord === nextOrd + 1);
    if (structure?.level) setNextLevel(structure.level);
    if (timeInfo.remainingTimeInCurrentLevel)
      setNextLevelTime(timeInfo.remainingTimeInCurrentLevel);
  }, [timeInfo]);

  const getPlayErrorName = (error: unknown) => {
    if (error instanceof DOMException) return error.name;
    if (error && typeof error === "object" && "name" in error) {
      return String((error as { name?: unknown }).name ?? "");
    }
    return "";
  };

  const isIgnorablePlayError = (name: string) =>
    name === "AbortError" || name === "NotAllowedError";

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || videoList.length === 0) return;

    const handlePlay = () => {
      const fileName = videoList[currentIndex]?.fileName;
      if (fileName) {
        startCurrentLog(fileName);
      }
    };

    const handleEnded = async () => {
      await endCurrentLog();

      if (videoList.length === 1) {
        videoElement.currentTime = 0;
        videoElement.play().catch((error) => {
          const name = getPlayErrorName(error);
          if (!isIgnorablePlayError(name)) console.error(error);
        });
      } else {
        setCurrentIndex((prev) => (prev + 1) % videoList.length);
      }
    };

    videoElement.addEventListener("play", handlePlay);
    videoElement.addEventListener("ended", handleEnded);
    return () => {
      videoElement.removeEventListener("play", handlePlay);
      videoElement.removeEventListener("ended", handleEnded);
    };
  }, [videoList, currentIndex]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !currentVideo) return;

    let cancelled = false;

    const playVideo = async () => {
      video.muted = isMuted;
      try {
        await video.play();
      } catch (error) {
        if (cancelled) return;

        const name = getPlayErrorName(error);

        if (name === "NotAllowedError") {
          video.muted = true;
          setIsMuted(true);
          await video.play().catch(() => {});
          return;
        }

        if (name !== "AbortError") {
          console.error(error);
        }
      }
    };

    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      playVideo();
    } else {
      video.addEventListener("canplay", playVideo, { once: true });
    }

    return () => {
      cancelled = true;
      video.removeEventListener("canplay", playVideo);
    };
  }, [currentIndex, isMuted, currentVideo]);

  const getNoticeText = (text: string) => {
    if (!text) return "";
    const lines = text.split("\n");
    if (lines.length <= 4) {
      return text.replace(/\n/g, "<br />");
    }
    const firstFourLines = lines.slice(0, 4);
    const lastLine = firstFourLines[3];
    const modifiedLastLine = lastLine.length > 0 ? lastLine + "..." : "...";
    firstFourLines[3] = modifiedLastLine;
    return firstFourLines.join("<br />");
  };

  return videoList.length > 0 ? (
    <AdVideoWrapper hasList={videoList.length > 0}>
      <AdVideoHeaderWrapper>
        <AdVideoHeaderLeftWrapper>
          {coverImage && <img src={coverImage} alt="logo" />}
          <div className="title">{data?.title}</div>
          <div className="sub-title">{data?.subTitle}</div>
        </AdVideoHeaderLeftWrapper>
        <AdVideoHeaderRightWrapper>{pub?.cafeName}</AdVideoHeaderRightWrapper>
      </AdVideoHeaderWrapper>
      <AdVideoBodyWrapper>
        <AdVideoBodyLeftWrapper>
          <AdVideoBodyVideoWrapper>
            {currentVideo && (
              <video
                key={currentVideo.id}
                ref={videoRef}
                src={`${AD_VIDEO_BASE_URL}/${currentVideo.fileName}`}
                controls={false}
                muted={isMuted}
                playsInline={true}
              />
            )}
            <AdVideoBodyVideoTimeBox>
              BREAK : {nextlevelTime}
            </AdVideoBodyVideoTimeBox>
          </AdVideoBodyVideoWrapper>
          <AdVideoBodyLeftBottomWrapper>
            <AdVideoBodyLeftBottomItemWrapper>
              <div className="title">PLAYERS</div>
              <div className="value">{`${getRemainingPlayerCount()} / ${getPlayerCount()}`}</div>
            </AdVideoBodyLeftBottomItemWrapper>
            <AdVideoBodyLeftBottomItemWrapper>
              <div className="title">TOTAL STACKS</div>
              <div className="value">{stack?.toLocaleString()}</div>
            </AdVideoBodyLeftBottomItemWrapper>
            <AdVideoBodyLeftBottomItemWrapper>
              <div className="title">AVG STACKS</div>
              <div className="value">{avgStack.toLocaleString()}</div>
            </AdVideoBodyLeftBottomItemWrapper>
            <AdVideoBodyLeftBottomItemWrapper>
              <div className="title">다음 레벨 : {nextlevel || ""}LV</div>
              <div className="sub-value">
                SB: {nextSmallBlind.toLocaleString()}
              </div>
              <div className="sub-value">
                BB: {nextBigBlind.toLocaleString()}
              </div>
              {nextAnte > 0 && (
                <div className="sub-value">
                  ANTE: {nextAnte.toLocaleString()}
                </div>
              )}
            </AdVideoBodyLeftBottomItemWrapper>
          </AdVideoBodyLeftBottomWrapper>
        </AdVideoBodyLeftWrapper>
        <AdVideoBodyRightWrapper>
          <div className="inner-wrapper">
            <AdVideoBodyRightTitle>PRIZE POOL</AdVideoBodyRightTitle>
            <AdVideoBodyRightListWrapper className={animClass}>
              {prizeList
                .slice(
                  currentPage * itemsPerPage,
                  currentPage * itemsPerPage + itemsPerPage,
                )
                .map((item, localIndex) => {
                  const index = currentPage * itemsPerPage + localIndex;
                  return (
                    <AdVideoPrizeListItem key={index}>
                      <div className="rank">
                        {index + 1}
                        <span>
                          {index === 0
                            ? "st"
                            : index === 1
                              ? "nd"
                              : index === 2
                                ? "rd"
                                : "th"}
                        </span>
                      </div>
                      <AdVideoPrizeListItemInfoBox>
                        <div className="prize">
                          {pub && data && prize(data, item, index)}{" "}
                          <span>{data && prizeType(item)}</span>
                        </div>
                        {item.subPrize && (
                          <div className="sub-prize">{item.subPrize}</div>
                        )}
                      </AdVideoPrizeListItemInfoBox>
                    </AdVideoPrizeListItem>
                  );
                })}
            </AdVideoBodyRightListWrapper>
            {data?.notice && (
              <AdVideoNoticeBox
                dangerouslySetInnerHTML={{
                  __html: getNoticeText(data.notice),
                }}
              />
            )}
          </div>
        </AdVideoBodyRightWrapper>
      </AdVideoBodyWrapper>
    </AdVideoWrapper>
  ) : (
    <></>
  );
};

export default AdVideo;
